import React from 'react';
import { View, ActivityIndicator } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { useGetLeaseEntrataDisabledQuery, useGetLeaseQuery, useGetLeaseBalanceQuery } from '@/store/api';
import { AsyncChargesSummaryCard } from '../cards/AsyncChargesSummaryCard';
import ChargeSummaryErrorCard from '../shared/ChargeSummaryErrorCard';
import { PAYMENT_CARD_LOADING_STYLES } from '../../common/cardStyleConstants';
import { useEntrataIntegrationDisabled } from '../../hooks/useEntrataIntegrationDisabled';

interface Props {
  leaseId: number;
  isSelected?: boolean;
  isConnection: boolean;
}

export const AsyncChargesSummary: React.FC<Props> = ({ leaseId, isSelected = false, isConnection }) => {
  const entrataDisabled = useEntrataIntegrationDisabled();

  const { data: balanceData } = useGetLeaseBalanceQuery(leaseId, { skip: !leaseId });

  const includeTransactions = false;
  const includePayments = true;
  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions, includePayments },
    { skip: !leaseId || entrataDisabled === true }
  );

  const leaseEntrataDisabledQuery = useGetLeaseEntrataDisabledQuery(
    { leaseId },
    { skip: !leaseId || entrataDisabled === false }
  );

  const isFetching = entrataDisabled ? leaseEntrataDisabledQuery.isFetching : leaseQuery.isFetching;
  const isError = entrataDisabled ? leaseEntrataDisabledQuery.isError : leaseQuery.isError;
  const isSuccess = entrataDisabled ? leaseEntrataDisabledQuery.isSuccess : leaseQuery.isSuccess;
  const data = entrataDisabled ? leaseEntrataDisabledQuery.data : leaseQuery.data;
  const refetch = entrataDisabled ? leaseEntrataDisabledQuery.refetch : leaseQuery.refetch;

  if (isFetching) {
    return (
      <View
        style={{
          marginBottom: PAYMENT_CARD_LOADING_STYLES.MARGIN_BOTTOM,
          padding: PAYMENT_CARD_LOADING_STYLES.PADDING,
          backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
          borderRadius: 12,
          minHeight: PAYMENT_CARD_LOADING_STYLES.MIN_HEIGHT,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ActivityIndicator size="small" color={RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY} />
      </View>
    );
  }

  if (isError) {
    return (
      <ChargeSummaryErrorCard
        leaseId={leaseId}
        isConnection={isConnection}
        onRefresh={() => refetch()}
      />
    );
  }

  if (isSuccess && data) {
    const response = (data as any)?.response || data;
    const lease = response?.lease || response?.lease?.id ? response.lease : response;
    const scheduledCharges = lease?.scheduledCharges || [];
    const statusType = lease?.statusType || '';
    const property = lease?.property || {};
    const users = lease?.users || [];
    const paymentsBlocked = !!lease?.paymentsBlocked;
    const autopayEnabled = Array.isArray(lease?.recurringPayments) && lease.recurringPayments.length > 0;
    const balanceDue = Number(balanceData?.balanceDue ?? 0);
    const balanceItems = balanceData?.balanceItems;
    const unitNumberSpace = lease?.unitNumberSpace || lease?.unitNumber || '';

    return (
      <AsyncChargesSummaryCard
        leaseId={leaseId}
        isSelected={isSelected}
        balanceDue={balanceDue}
        balanceItems={balanceItems}
        propertyMarketingName={property?.marketingName}
        paymentsBlocked={paymentsBlocked}
        autopayEnabled={autopayEnabled}
        scheduledCharges={scheduledCharges}
        statusType={statusType}
        users={users}
        unitNumberSpace={unitNumberSpace}
        propertyId={property?.id}
        isConnection={isConnection}
        onRefresh={() => refetch()}
      />
    );
  }

  return null;
};


